import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

const NavKeyboardContext = createContext(null)

export const useNavKeyboard = () => useContext(NavKeyboardContext)

const pickTabStopKey = (entries) => {
  if (!entries.length) return null

  const active = entries.filter((entry) => entry.isActive)
  if (!active.length) {
    return entries.find((entry) => entry.parentKey == null)?.key ?? entries[0].key
  }

  return active.reduce((best, entry) => (entry.level >= best.level ? entry : best)).key
}

const activeSignature = (registry) =>
  [...registry.values()]
    .filter((entry) => entry.isActive)
    .map((entry) => `${entry.key}:${entry.level}`)
    .sort()
    .join('|')

export const NavKeyboardProvider = ({ children }) => {
  const registryRef = useRef(new Map())
  const [activeSig, setActiveSig] = useState('')
  const [tabStopKey, setTabStopKey] = useState(null)

  const syncActiveSig = useCallback(() => {
    const next = activeSignature(registryRef.current)
    setActiveSig((prev) => (prev === next ? prev : next))
  }, [])

  const register = useCallback(
    (entry) => {
      registryRef.current.set(entry.key, entry)
      syncActiveSig()
      return () => {
        // Ignore stale cleanups after a newer register() for the same key.
        if (registryRef.current.get(entry.key) !== entry) return

        registryRef.current.delete(entry.key)

        // Defer: same-key re-register in this commit must not reset roving tabStop.
        queueMicrotask(() => {
          if (registryRef.current.has(entry.key)) {
            syncActiveSig()
            return
          }

          syncActiveSig()
          setTabStopKey((current) =>
            current === entry.key ? pickTabStopKey([...registryRef.current.values()]) : current,
          )
        })
      }
    },
    [syncActiveSig],
  )

  const getEntry = useCallback((key) => registryRef.current.get(key) ?? null, [])

  const getSiblings = useCallback((parentKey) => {
    return [...registryRef.current.values()]
      .filter((entry) => entry.parentKey === parentKey)
      .sort((a, b) => a.index - b.index)
  }, [])

  const getChildren = useCallback((parentKey) => getSiblings(parentKey), [getSiblings])

  const focusKey = useCallback((key) => {
    const entry = registryRef.current.get(key)
    if (!entry) return

    setTabStopKey(key)
    requestAnimationFrame(() => {
      entry.getElement()?.focus?.({ preventScroll: true })
    })
  }, [])

  // Land Tab on deepest active item when the route-active set changes.
  // Skip empty sig (registry flicker during effect re-register).
  useEffect(() => {
    if (!activeSig) return

    const next = pickTabStopKey([...registryRef.current.values()])
    if (next) setTabStopKey(next)
  }, [activeSig])

  const value = useMemo(
    () => ({
      register,
      getEntry,
      getSiblings,
      getChildren,
      focusKey,
      tabStopKey,
      setTabStopKey,
    }),
    [register, getEntry, getSiblings, getChildren, focusKey, tabStopKey],
  )

  return <NavKeyboardContext.Provider value={value}>{children}</NavKeyboardContext.Provider>
}

export default NavKeyboardContext
